import { graphql } from "babel-plugin-relay/macro";
import { useTitle } from "../lib/title";
import {
    PreloadedQuery,
    useFragment,
    useMutation,
    usePreloadedQuery,
    useQueryLoader,
} from "react-relay";
import { selfServiceSettingsQuery } from "./__generated__/selfServiceSettingsQuery.graphql";
import { useEffect, useState } from "react";
import { Form, Button, Alert, Dropdown } from "react-bootstrap";
import { TextWithTooltip } from "../components/TextWithTooltip";
import styles from "./selfServiceSettings.module.scss";
import { useSearchParams } from "react-router-dom";
import { BadRequestErrorPage } from "./ErrorPage";
import { selfServiceSettingsSaveMutation } from "./__generated__/selfServiceSettingsSaveMutation.graphql";
import { selfServiceSettingsSetStatusMutation } from "./__generated__/selfServiceSettingsSetStatusMutation.graphql";
import {
    selfServiceSettings_statusFragment$key,
    UJStatuses,
} from "./__generated__/selfServiceSettings_statusFragment.graphql";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { selfServiceSettings_mainSettingsFragment$key } from "./__generated__/selfServiceSettings_mainSettingsFragment.graphql";

export const SelfServiceSettingsQuery = graphql`
    query selfServiceSettingsQuery($ujId: Int!) {
        userJourney(ujId: $ujId) {
            ...selfServiceSettings_mainSettingsFragment
            ...selfServiceSettings_statusFragment
            isSelfService
        }
    }
`;

const SelfServiceSettingsMainSettingsFragment = graphql`
    fragment selfServiceSettings_mainSettingsFragment on UserJourney {
        id
        name
        cutoff
        cutOffOK
        alertOnSlow
    }
`;

const SelfServiceSettingsStatusFragment = graphql`
    fragment selfServiceSettings_statusFragment on UserJourney {
        id
        status
    }
`;

export function SelfServiceSettingsPage() {
    useTitle("JourneyScribe Settings");

    const [searchParams] = useSearchParams();
    const ujID = searchParams.get("uj");

    if (!ujID) {
        // overrides of ujID already check that it is a valid number
        return <BadRequestErrorPage />;
    }

    return <SelfServiceSettingsQueryLoader ujID={parseInt(ujID)} />;
}

function SelfServiceSettingsQueryLoader({ ujID }: { ujID: number }) {
    const [selfServiceSettingsQueryReference, selfServiceSettingsQueryLoader] =
        useQueryLoader<selfServiceSettingsQuery>(SelfServiceSettingsQuery);

    useEffect(() => {
        selfServiceSettingsQueryLoader({ ujId: ujID });
    }, [selfServiceSettingsQueryLoader, ujID]);

    return (
        <>
            {selfServiceSettingsQueryReference && (
                <SelfServiceSettingsContent
                    selfServiceSettingsQueryReference={
                        selfServiceSettingsQueryReference
                    }
                    ujID={ujID}
                />
            )}
        </>
    );
}

function SelfServiceSettingsContent({
    selfServiceSettingsQueryReference,
    ujID,
}: {
    selfServiceSettingsQueryReference: PreloadedQuery<selfServiceSettingsQuery>;
    ujID: number;
}) {
    const data = usePreloadedQuery<selfServiceSettingsQuery>(
        SelfServiceSettingsQuery,
        selfServiceSettingsQueryReference,
    );

    useEffect(() => {
        if (!data.userJourney.isSelfService) {
            window.location.assign(
                "/portal2/userJourney/home" + window.location.search,
            );
        }
    }, [data.userJourney.isSelfService]);

    return (
        <>
            {data.userJourney.isSelfService && (
                <div>
                    <div className={styles.selfServiceSettingsContainer}>
                        <h1>JourneyScribe Settings</h1>

                        <div className={styles.statusSection}>
                            <SelfServiceStatus
                                userJourney={data.userJourney}
                                ujID={ujID}
                            />
                        </div>

                        <div className={styles.statusSection}>
                            <SelfServiceSettingsForm
                                userJourney={data.userJourney}
                                ujID={ujID}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function SelfServiceSettingsForm({
    ujID,
    userJourney,
}: {
    ujID: number;
    userJourney: selfServiceSettings_mainSettingsFragment$key;
}) {
    const userJourneySettings = useFragment(
        SelfServiceSettingsMainSettingsFragment,
        userJourney,
    );

    const [saveSettings, isSaving] =
        useMutation<selfServiceSettingsSaveMutation>(graphql`
            mutation selfServiceSettingsSaveMutation(
                $ujID: Int!
                $name: String!
                $cutoff: Int
                $cutOffOK: Boolean!
                $alertOnSlow: Boolean!
            ) {
                setSelfServiceUJSettings(
                    ujData: {
                        id: $ujID
                        name: $name
                        cutoff: $cutoff
                        cutOffOK: $cutOffOK
                        alertOnSlow: $alertOnSlow
                    }
                ) {
                    userJourney {
                        ...selfServiceSettings_mainSettingsFragment
                    }
                }
            }
        `);

    const [formData, setFormData] = useState({
        name: userJourneySettings.name,
        cutoff: userJourneySettings.cutoff,
        cutOffOK: userJourneySettings.cutOffOK,
        alertOnSlow: userJourneySettings.alertOnSlow,
    });

    useEffect(() => {
        setFormData({
            name: userJourneySettings.name,
            cutoff: userJourneySettings.cutoff,
            cutOffOK: userJourneySettings.cutOffOK,
            alertOnSlow: userJourneySettings.alertOnSlow,
        });
    }, [userJourneySettings]);

    useEffect(() => {
        setValidationError("");
        setShowSuccess(false);
    }, [ujID]);

    const [showSuccess, setShowSuccess] = useState(false);
    const [validationError, setValidationError] = useState("");

    const handleSave = () => {
        if (formData.cutoff && formData.cutoff < 0) {
            setValidationError("Cutoff must be a positive integer.");
            return;
        }

        saveSettings({
            variables: {
                ujID: ujID,
                name: formData.name,
                cutoff: formData.cutoff,
                cutOffOK: formData.cutOffOK ?? false,
                alertOnSlow: formData.alertOnSlow,
            },
            onCompleted(_, errors) {
                if (errors && errors.length > 0) {
                    setValidationError(
                        "Failed to save settings, please try again.",
                    );
                } else {
                    setShowSuccess(true);
                }
            },
            onError(_) {
                setValidationError(
                    "Failed to save settings, please try again.",
                );
            },
        });
    };

    return (
        <Form className={styles.formContainer}>
            <Form.Group className={styles.formGroup}>
                <Form.Label htmlFor="ujName">User Journey Name</Form.Label>
                <Form.Control
                    type="text"
                    id="ujName"
                    value={formData.name}
                    maxLength={40}
                    onChange={(e) =>
                        setFormData({
                            ...formData,
                            name: e.target.value,
                        })
                    }
                />
            </Form.Group>

            <Form.Group className={styles.formGroup}>
                <Form.Label htmlFor="cutoff">
                    <TextWithTooltip name={"Cutoff"} />
                </Form.Label>
                <Form.Control
                    id="cutoff"
                    type="number"
                    min={1}
                    step={1}
                    value={formData.cutoff ?? ""}
                    onChange={(e) =>
                        setFormData({
                            ...formData,
                            cutoff: e.target.value
                                ? parseInt(e.target.value, 10)
                                : null,
                        })
                    }
                />
            </Form.Group>

            <Form.Group className={styles.formGroup}>
                <div className="form-check form-switch">
                    <Form.Check
                        type="switch"
                        id="cutOffOK-switch"
                        label={<TextWithTooltip name={"Cutoff OK"} />}
                        checked={formData.cutOffOK ?? false}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                cutOffOK: e.target.checked,
                            })
                        }
                    />
                </div>
            </Form.Group>

            <Form.Group className={styles.formGroup}>
                <div className="form-check form-switch">
                    <Form.Check
                        type="switch"
                        id="alertOnSlow-switch"
                        label={<TextWithTooltip name={"Alert On Slow"} />}
                        checked={formData.alertOnSlow}
                        onChange={(e) =>
                            setFormData({
                                ...formData,
                                alertOnSlow: e.target.checked,
                            })
                        }
                    />
                </div>
            </Form.Group>

            <Button
                variant="tribe-primary"
                className={styles.saveButton}
                onClick={handleSave}
                disabled={isSaving}
            >
                Save Changes
            </Button>

            {showSuccess && (
                <SuccessToast clearToast={() => setShowSuccess(false)} />
            )}

            {validationError && (
                <ErrorToast
                    errorMessage={validationError}
                    clearToast={() => setValidationError("")}
                />
            )}
        </Form>
    );
}

function SelfServiceStatus({
    userJourney,
    ujID,
}: {
    userJourney: selfServiceSettings_statusFragment$key;
    ujID: number;
}) {
    const userJourneyStatus = useFragment(
        SelfServiceSettingsStatusFragment,
        userJourney,
    );

    const [setStatus] = useMutation<selfServiceSettingsSetStatusMutation>(
        graphql`
            mutation selfServiceSettingsSetStatusMutation(
                $ujID: Int!
                $status: UJStatuses!
            ) {
                setSelfServiceUJStatus(uj: { id: $ujID, status: $status }) {
                    userJourney {
                        ...selfServiceSettings_statusFragment
                    }
                }
            }
        `,
    );

    const [showSuccess, setShowSuccess] = useState(false);
    const [showFailure, setShowFailure] = useState(false);

    type KnownUJStatuses = Exclude<UJStatuses, "%future added value">;
    const ujStatusMapping: Record<
        KnownUJStatuses | "UNKNOWN",
        {
            displayStatus: string;
            variant: string;
        }
    > = {
        RUNNING: {
            displayStatus: "Running",
            variant: "success",
        },
        PAUSED: {
            displayStatus: "Paused",
            variant: "warning",
        },
        OBSOLETE: {
            displayStatus: "Stopped",
            variant: "danger",
        },
        UNKNOWN: {
            displayStatus: "Unknown",
            variant: "secondary",
        },
    };

    let currentStatusKey: UJStatuses | "UNKNOWN" = userJourneyStatus.status;
    if (currentStatusKey === "%future added value") {
        // support for newer UJ statuses that are not present
        // we don't expect these values yet, but we need to set it to something to avoid errors
        currentStatusKey = "UNKNOWN";
    }

    const [selectedStatusKey, setSelectedStatusKey] = useState<
        KnownUJStatuses | "UNKNOWN"
    >(currentStatusKey as KnownUJStatuses | "UNKNOWN");

    useEffect(() => {
        setSelectedStatusKey(currentStatusKey as KnownUJStatuses | "UNKNOWN");
    }, [currentStatusKey]);

    useEffect(() => {
        setShowFailure(false);
        setShowSuccess(false);
    }, [ujID]);

    return (
        <div>
            <div className={styles.statusSectionInner}>
                <TextWithTooltip name="Journey Status" />
                <Dropdown
                    onSelect={(eventKey) => {
                        if (eventKey !== null) {
                            setSelectedStatusKey(eventKey as KnownUJStatuses);
                        }
                    }}
                >
                    <Dropdown.Toggle
                        title={ujStatusMapping[selectedStatusKey].displayStatus}
                        variant={ujStatusMapping[selectedStatusKey].variant}
                    >
                        {ujStatusMapping[selectedStatusKey].displayStatus}

                        <FontAwesomeIcon
                            style={{ marginLeft: "1em" }}
                            icon={faChevronDown}
                        />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item
                            active={selectedStatusKey === "RUNNING"}
                            eventKey="RUNNING"
                        >
                            Running
                        </Dropdown.Item>
                        <Dropdown.Item
                            active={selectedStatusKey === "PAUSED"}
                            eventKey="PAUSED"
                        >
                            Paused
                        </Dropdown.Item>
                        <Dropdown.Item
                            active={selectedStatusKey === "OBSOLETE"}
                            eventKey="OBSOLETE"
                        >
                            Stopped
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <Button
                variant="tribe-primary"
                className={styles.saveButton}
                onClick={() => {
                    if (
                        selectedStatusKey &&
                        selectedStatusKey !== currentStatusKey
                    ) {
                        setStatus({
                            variables: {
                                ujID,
                                status: selectedStatusKey as KnownUJStatuses,
                            },
                            onCompleted(_, errors) {
                                if (errors && errors.length > 0) {
                                    setShowFailure(true);
                                } else {
                                    setShowSuccess(true);
                                }
                            },
                            onError(_) {
                                setShowFailure(true);
                            },
                        });
                    }
                }}
                disabled={selectedStatusKey === currentStatusKey}
            >
                Save Changes
            </Button>

            {showSuccess && (
                <SuccessToast clearToast={() => setShowSuccess(false)} />
            )}

            {showFailure && (
                <ErrorToast
                    errorMessage={"Failed to update status, please try again."}
                    clearToast={() => setShowFailure(false)}
                />
            )}
        </div>
    );
}

interface ToastProps {
    clearToast: () => void;
}

function SuccessToast({ clearToast }: ToastProps) {
    return (
        <Alert
            variant="success"
            className={styles.toastMessage}
            onClose={() => clearToast()}
            dismissible
        >
            Settings saved successfully!
        </Alert>
    );
}

interface ErrorToastProps extends ToastProps {
    errorMessage: string;
}

function ErrorToast({ clearToast, errorMessage }: ErrorToastProps) {
    return (
        <Alert
            variant="danger"
            className={styles.toastMessage}
            onClose={() => clearToast()}
            dismissible
        >
            {errorMessage}
        </Alert>
    );
}
